import { JSX, useState } from "react";
import { ActiveLabel, Bar, BarChart, BaseTickContentProps, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { NetworkCompositionData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { networkCompositionColors, widgetTooltipCursor } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { printNumberAsString } from "@app/shared/waffles";
import { useHoverBoundary } from "@app/shared/hooks/useHoverBoundary";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { ContractDisplayInformation } from "@app/overviews/network-benchmarking-dashboard/filters/shared/ContractDisplayInformation";

interface NetworkCompositionProps {
  customYAxisTick?: (props: BaseTickContentProps) => JSX.Element;
  data: NetworkCompositionData[];
  getContractDisplayInformation: (contractId: string) => ContractDisplayInformation;
  loading: boolean;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
  ) => void;
  selectedVolumeIndicators: ClaimsActivityVolumeLabelEnum[];
}

const NetworkComposition = (props: NetworkCompositionProps): JSX.Element => {
  const [bar, setBar] = useState<ClaimsActivityVolumeLabelEnum>(null);
  const { isHovered: isChartHovered, ref: chartRef } = useHoverBoundary<HTMLDivElement>();
  const onNetworkCompositionClick = (activeLabel: ActiveLabel): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!activeLabel) {
      const contractData: NetworkCompositionData = props.data.find(x => x.contractId === activeLabel);

      if (!!contractData && !!contractData[`${bar}Count`]) {
        props.onDashboardDetailsViewClick(
          BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin,
          contractData[`${bar}Count`] as number,
          contractData?.versionId,
          null,
          bar
        );
      }
    }
  }

  const customTooltip = (customTooltipProps: TooltipContentProps<ValueType, string | number>): JSX.Element => {
    if (customTooltipProps.payload?.length && !!bar) {
      const networkCompositionData: NetworkCompositionData = props.data.find(x => x.contractId === customTooltipProps.label);
      const contractDisplayInformation: ContractDisplayInformation = props.getContractDisplayInformation(String(customTooltipProps.label));

      if (!!networkCompositionData && !!contractDisplayInformation) {
        return (
          <div className="qes-dashboard__custom-tooltip">
            <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.parentOrganizationName}</p>
            <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.networkName}</p>
            <p className="qes-dashboard__tooltip-title mb-0">{bar} Providers</p>
            Percentage: {networkCompositionData[`${bar}Percentage`]}% <br />
            Total in Category: {printNumberAsString(networkCompositionData[`${bar}Count`] as number, "---")} <br />
            Total Providers: {printNumberAsString(networkCompositionData.totalProviderCount, "---")}
          </div>
        );
      }
    }
  }

  const formatLegendText = (value: string): JSX.Element => {
    return (
      <span className="me-2 text-default">
        {value.replace(/DisplayPercentage/g, '')}
      </span>
    );
  };

  const formatPercentageLabel = (percentage: number): string => {
    if (percentage >= 7) {
      return printNumberAsString(percentage, "0") + "%";
    } else {
      return;
    }
  }

  const getBarColorByVolumeLabel = (volumeLabel: ClaimsActivityVolumeLabelEnum): string => {
    switch (volumeLabel) {
      case ClaimsActivityVolumeLabelEnum.Core:
        return networkCompositionColors.darkBlue;
      case ClaimsActivityVolumeLabelEnum.Standard:
        return networkCompositionColors.brightCyan;
      case ClaimsActivityVolumeLabelEnum.Peripheral:
        return networkCompositionColors.water;
      case ClaimsActivityVolumeLabelEnum.RxOnly:
        return networkCompositionColors.cream;
      case ClaimsActivityVolumeLabelEnum.InsufficientData:
        return networkCompositionColors.lightYellow;
      case ClaimsActivityVolumeLabelEnum.Ghost:
        return networkCompositionColors.marigold;
      default:
        return "";
    }
  }

  const getTextColorByVolumeLabel = (volumeLabel: ClaimsActivityVolumeLabelEnum): string => {
    switch (volumeLabel) {
      case ClaimsActivityVolumeLabelEnum.Core:
      case ClaimsActivityVolumeLabelEnum.Ghost:
        return "white";
      default:
        return "black";
    }
  }

  const tickFormatter = (): string => {
    // ticks are needed to correctly display the vertical cartesian grid,
    // despite the fact that we don't actually want to display them
    return "";
  }

  return (
    <DashboardTile
      loading={props.loading}
      subtitle="Each bar can be clicked to reveal additional details."
      title="Network Composition by Claims Insights"
      widthClass="col-md-12"
    >
      <div
        ref={chartRef}
        style={{ height: "300px", width: "100%" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            barCategoryGap="20%"
            data={props.data}
            layout="vertical"
            margin={{
              bottom: 0,
              left: 0,
              right: 0,
              top: 20,
            }}
            onClick={(e) => { onNetworkCompositionClick(e.activeLabel) }}
          >
            <CartesianGrid strokeDasharray="1 0" horizontal={false} />
            <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 15 }} formatter={formatLegendText} />
            <XAxis type="number" domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickLine={false} tickFormatter={tickFormatter} axisLine={false} />
            <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
            <Tooltip content={(customTooltipProps) => customTooltip(customTooltipProps)} cursor={widgetTooltipCursor} active={isChartHovered ? undefined : false} />
            {Object.values(ClaimsActivityVolumeLabelEnum).filter(x => props.selectedVolumeIndicators?.includes(x)).map((volumeLabel, volumeLabelIndex) => (
              <Bar
                onMouseOver={() => setBar(volumeLabel)}
                dataKey={`${volumeLabel}DisplayPercentage`}
                key={volumeLabelIndex}
                fill={`#${getBarColorByVolumeLabel(volumeLabel as ClaimsActivityVolumeLabelEnum)}`}
                cursor="pointer"
                stackId="a"
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={`${volumeLabel}Percentage`}
                  formatter={(percentage: number) => formatPercentageLabel(percentage)}
                  position="inside"
                  fill={getTextColorByVolumeLabel(volumeLabel)}
                  style={{ fontSize: "12px" }}
                  cursor="pointer"
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardTile>
  )
}

export default NetworkComposition;
